import { memo, ReactNode, useCallback, useMemo, useState } from "react";
import { v1 } from "uuid";
import { FullscreenLoaderContainer } from "./FullscreenLoaderContainer";
import { FullscreenLoaderContext, FullscreenLoaderContextProps, FullscreenLoaderProcessProps } from "./FullscreenLoaderContext";

export type FullscreenLoaderProviderProps = {
  children: ReactNode
}

export const FullscreenLoaderProvider = memo<FullscreenLoaderProviderProps>(({ children }) => {
  const processesStorage = useMemo(() => ({ processes: [] as FullscreenLoaderProcessProps[] }), []);
  const [ processesOnUI, setProcessesOnUI ] = useState<FullscreenLoaderProcessProps[]>(processesStorage.processes);

  const push = useCallback((props: FullscreenLoaderProcessProps) => {
    const existingIndex = processesStorage.processes.findIndex((item) => item.processId === props.processId);
    const index = existingIndex !== -1 ? existingIndex : processesStorage.processes.length;

    const newProcessProps: FullscreenLoaderProcessProps = {
      ...processesStorage.processes[existingIndex],
      ...props,
      processId: props.processId || v1(),
    };
    const newProcesses = [
      ...processesStorage.processes.slice(0, index),
      newProcessProps,
      ...processesStorage.processes.slice(index + 1),
    ];

    Object.freeze(newProcessProps);
    Object.freeze(newProcesses);

    processesStorage.processes = newProcesses;
    setProcessesOnUI(newProcesses);

    return newProcessProps;
  }, [ processesStorage, setProcessesOnUI ]);

  const remove = useCallback(
    (processId: string | undefined) => {
      const newProcesses = processesStorage.processes.filter((props) => props.processId !== processId);
      Object.freeze(newProcesses);
      processesStorage.processes = newProcesses;
      setProcessesOnUI(newProcesses);
    },
    [ processesStorage, setProcessesOnUI ],
  );

  const providerValue = useMemo(
    (): FullscreenLoaderContextProps => ({
      processes: processesOnUI,
      push,
      remove,
    }),
    [ processesOnUI, push, remove ],
  );

  return (
    <FullscreenLoaderContext.Provider value={providerValue}>
      {children}
      <FullscreenLoaderContainer />
    </FullscreenLoaderContext.Provider>
  );
});
